import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { HexColorPicker } from "react-colorful";

const ColorPickerModal = ({
  open,
  title = "Seleccionar color",
  color = "#000000",
  onApply,
  onClose,
}) => {
  const [draft, setDraft] = useState(color);

  useEffect(() => {
    if (open) setDraft(color || "#000000");
  }, [open, color]);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDraft(value.startsWith("#") ? value : `#${value}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
      PaperProps={{
        sx: { 
          borderRadius: 3, 
          overflow: "hidden",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
        },
      }}
    >
      <DialogTitle 
        sx={{ 
          fontWeight: 700, 
          fontSize: "1.25rem",
          color: "#1f2937",
          borderBottom: "1px solid #e5e7eb",
          pb: 2
        }}
      >
        {title}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <HexColorPicker color={draft} onChange={setDraft} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#374151" }}>
              Código HEX
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Hexadecimal"
              value={draft.replace("#", "")}
              onChange={handleHexChange}
              InputProps={{ 
                startAdornment: <span style={{ marginRight: 8, color: "#6b7280", fontWeight: 600 }}>#</span>,
                sx: {
                  borderRadius: 2,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e5e7eb",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                    borderWidth: 2,
                  },
                }
              }}
              sx={{
                "& .MuiInputLabel-root": {
                  color: "#6b7280",
                  "&.Mui-focused": {
                    color: "#1976d2",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ 
            p: 2, 
            borderRadius: 2, 
            border: "1px solid #e5e7eb", 
            display: "flex", 
            alignItems: "center", 
            gap: 2,
            bgcolor: "#f9fafb"
          }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: 1.5, 
              border: "2px solid #e5e7eb", 
              bgcolor: draft,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
                Vista previa
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                {draft}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          variant="text" 
          onClick={onClose}
          sx={{
            color: "#6b7280",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#f3f4f6",
            },
          }}
        >
          Cancelar
        </Button>
        <Button 
          variant="contained" 
          onClick={() => {
            onApply?.(draft);
            onClose();
          }}
          sx={{
            bgcolor: "#1976d2",
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            "&:hover": { 
              bgcolor: "#1565c0" 
            },
          }}
        >
          Aplicar color
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorPickerModal;
